import { useState, useEffect, useContext } from 'react';
import { ProgressBar } from 'primereact/progressbar';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { formatFileSize, fetchWithAuthentication } from '@/Helpers/general';
import ContentLoadingTemplate from '@/Components/Templates/ContentLoadingTemplate';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { CaseContext } from '@/Components/Context/CaseContext';

const OCR = ({ formLoading, currentRequest }) => {
    const { getCaseData, refreshBtnLoading, ocrFile } = useContext(CaseContext);

    const [divWidth, setDivWidth] = useState(0);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfData, setPdfData] = useState(null);
    const [name, setName] = useState(null);
    const [url, setUrl] = useState(null);
    const [processedUrl, setProcessedUrl] = useState(null);
    const [ocrFileStatus, setOcrFileStatus] = useState(null);
    const [mapped, setMapped] = useState(null);
    const [total, setTotal] = useState(null);
    const [size, setSize] = useState(null);
    const [extension, setExtension] = useState(null);
    const [loading, setLoading] = useState(true);
    const [versionCheck, setVersionCheck] = useState(false);
    const [percent, setPercent] = useState(0);

    useEffect(() => {
        setDivWidth(window.innerWidth * 0.6);
    }, []);

    useEffect(() => {
        if (ocrFile) {
            setName(ocrFile.name);
            setMapped(ocrFile.ocr_fields_mapped);
            setTotal(ocrFile.ocr_fields_total);
            setUrl(ocrFile.view_url);
            setProcessedUrl(ocrFile.processed_url);
            setOcrFileStatus(ocrFile.status);
            setSize(ocrFile.size);
            setExtension(ocrFile.extension);

            if (ocrFile.ocr_fields_mapped && ocrFile.ocr_fields_total) {
                setPercent(((100 * ocrFile.ocr_fields_mapped) / ocrFile.ocr_fields_total).toFixed(2));
            }
        }
    }, [ocrFile]);

    useEffect(() => {
        const fetchPdf = async (pdfUrl) => {
            const response = await fetchWithAuthentication(pdfUrl);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setPdfData(objectUrl);
        }

        setLoading(true);
        if (ocrFile && !ocrFile.is_processing) {
            fetchPdf(versionCheck ? url : processedUrl)
                .catch(console.error)
        }
        setLoading(false);
    }, [versionCheck, ocrFileStatus]);

    function firstPage() {
        setPageNumber(1);
    }

    function nextPage() {
        setPageNumber(pageNumber + 1 >= numPages ? numPages : pageNumber + 1);
    }
    function prevPage() {
        setPageNumber(pageNumber - 1 <= 1 ? 1 : pageNumber - 1);
    }

    function lastPage() {
        setPageNumber(numPages);
    }

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    async function changeVersion() {
        setVersionCheck(!versionCheck);
    }

    return (
        <div className="general-form relative claims-summary">
            {ocrFile &&
                <div className="flex flex-column">
                    {(ocrFile && ocrFile?.is_processing)
                        ? <div className="text-center my-5">
                            <p>OCR file is currently in processing. Use the refresh button to check if the file is ready.</p>
                            <Button className="my-3" label="Refresh" icon="pi pi-refresh" onClick={getCaseData} loading={refreshBtnLoading} />
                        </div>

                        : <>
                            <div className="flex flex-row justify-content-center align-items-center mx-auto">
                                <label style={{ fontWeight: 'bold' }}>Detected</label>

                                <InputSwitch
                                    className="mx-3"
                                    tooltipOptions={{ showOnDisabled: true }}
                                    checked={versionCheck}
                                    onChange={(e) => {
                                        changeVersion();
                                    }}
                                />
                                <label style={{ fontWeight: 'bold' }}>Original</label>
                            </div>

                            <div className="flex flex-column justify-content-center mx-auto mt-3">
                                <table>
                                    <tbody>
                                        <tr className="col-12 md:col-6 lg:col-4">
                                            <td className="my-2 font-bold text-right">File opened:</td>
                                            <td className="my-2 text-left">"{name}"</td>
                                        </tr>
                                        <tr className="col-12 md:col-6 lg:col-4">
                                            <td className="my-2 font-bold text-right">File size:</td>
                                            <td className="my-2 text-left">{formatFileSize(size)}</td>
                                        </tr>
                                        <tr className="col-12 md:col-6 lg:col-4">
                                            <td className="my-2 font-bold text-right">File extension:</td>
                                            <td className="my-2 text-left">{extension}</td>
                                        </tr>
                                        <tr className="col-12 md:col-6 lg:col-4">
                                            <td className="my-2 font-bold text-right">Import progress:</td>
                                            <td className="my-2 text-left">
                                                {Boolean(mapped && total) && (
                                                    <>
                                                        {mapped} / {total} fields found
                                                    </>
                                                )}
                                                {!Boolean(mapped) && <span>no fields found</span>} {ocrFile?.status == 'ocr_done_fail' && <span>
                                                    (<span className="text-error">OCR failed</span>)
                                                </span>}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div style={{ width: '690px' }} className="flex flex-column">
                                    {Boolean(mapped && total) && (
                                        <div className="my-2 flex flex-column">
                                            <div className="mx-8 mt-2">
                                                <ProgressBar value={percent} />
                                            </div>
                                        </div>
                                    )}

                                    {(ocrFile?.status == 'ocr_done_ok' || versionCheck) && <div className="my-3 flex flex-row justify-content-center align-items-center">
                                        <Button className="mx-2 grey-nav-btn grey-nav-btn-hover" icon="pi pi-fast-backward" onClick={firstPage} />
                                        <Button className="grey-nav-btn grey-nav-btn-hover mx-2" icon="pi pi-step-backward" onClick={prevPage} />
                                        <div className="m-2 flex justify-content-center ">
                                            Page {pageNumber} of {numPages}
                                        </div>
                                        <Button className="grey-nav-btn grey-nav-btn-hover mx-2" icon="pi pi-step-forward" onClick={nextPage} />
                                        <Button className="grey-nav-btn grey-nav-btn-hover mx-2" icon="pi pi-fast-forward" onClick={lastPage} />
                                    </div>}
                                </div>
                            </div>

                            <div style={{ height: '84vw', width: '60vw' }} className="flex flex-column justify-content-center align-items-center mx-auto my-5">
                                {loading && <div>Loading PDF...</div>}
                                {!loading && (
                                    <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess} imageResourcesPath="/img/">
                                        <Page width={divWidth} pageNumber={pageNumber} />
                                    </Document>
                                )}
                            </div>
                            </>
                    }
                </div>
            }
            {formLoading && <ContentLoadingTemplate />}
        </div>
    );
};

export default OCR;
